import { useSuspenseQuery } from "@apollo/client";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Copyable,
  DateUI,
  PrimaryFullScreenSpinner,
  Txt,
  UserProfile,
} from "@repo/ui";
import { NotFoundError, prettyPrintEnum } from "@repo/utils";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { Suspense } from "react";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute("/__auth/notifications_/$notificationId")({
  head: () => opsHead("Notification"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { notificationId } = useParams({ strict: false });

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminNotificationDetail($id: ID!) {
        node(id: $id) {
          ... on Notification {
            id
            shortRef
            createdAt
            kind
            recoveryPoint
            idempotencyKey
            content
            deliveries {
              id
              shortRef
              createdAt
              recipientEmailAddress
              recoveryPoint
              recipientUserProfile {
                ...UserProfileUI
              }
              attempts {
                id
                createdAt
                result
                errorMessage
                postmarkMessageId
              }
            }
          }
        }
      }
    `),
    {
      variables: {
        id: notificationId ?? "",
      },
    },
  );

  const notification = data.node;
  if (!notification || notification.__typename !== "Notification") {
    throw new NotFoundError();
  }

  useOpsTitle(`Notification #${notification.shortRef}`);

  return (
    <AdminPageContainer>
      <AdminPageHeader title={`Notification #${notification.shortRef}`} />

      <div className="flex flex-col gap-6">
        <div className="flex flex-row gap-6">
          <div className="flex-1">
            <DetailRow
              label="ID"
              value={
                <Copyable>
                  <span className="font-mono text-sm">{notification.id}</span>
                </Copyable>
              }
            />
            <DetailRow
              label="Created"
              value={
                <DateUI date={notification.createdAt} variant="DATETIME_TZ" />
              }
            />
            <DetailRow
              label="Kind"
              value={<Txt variant="mono">{notification.kind}</Txt>}
            />
            <DetailRow
              label="Status"
              value={prettyPrintEnum(notification.recoveryPoint)}
            />
            {notification.idempotencyKey && (
              <DetailRow
                label="Idempotency key"
                value={
                  <span className="font-mono text-sm">
                    {notification.idempotencyKey}
                  </span>
                }
              />
            )}
          </div>

          <div className="flex-1">
            <div className="mb-2">
              <Txt variant="detail-label">Content</Txt>
            </div>
            {notification.content ? (
              <pre className="overflow-x-auto bg-secondary p-4 text-sm">
                {JSON.stringify(notification.content, null, 2)}
              </pre>
            ) : (
              <Txt variant="body" tone="secondary">
                No content
              </Txt>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <Txt variant="section-title">
            Deliveries ({notification.deliveries.length})
          </Txt>

          {notification.deliveries.length === 0 ? (
            <Txt variant="body" tone="secondary">
              No deliveries
            </Txt>
          ) : (
            notification.deliveries.map((delivery) => (
              <Card key={delivery.id}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between text-base">
                    <span>Delivery #{delivery.shortRef}</span>
                    <span className="font-normal text-secondary text-sm">
                      {prettyPrintEnum(delivery.recoveryPoint)}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                  <div className="flex flex-row gap-6">
                    <div className="flex-1">
                      <DetailRow
                        label="ID"
                        value={
                          <Copyable>
                            <span className="font-mono text-sm">
                              {delivery.id}
                            </span>
                          </Copyable>
                        }
                      />
                      <DetailRow
                        label="Created"
                        value={
                          <DateUI
                            date={delivery.createdAt}
                            variant="DATETIME_TZ"
                          />
                        }
                      />
                      <DetailRow
                        label="Email"
                        value={delivery.recipientEmailAddress}
                      />
                      <DetailRow
                        label="Recipient"
                        value={
                          <UserProfile {...delivery.recipientUserProfile} />
                        }
                      />
                    </div>

                    <div className="flex-1">
                      <div className="mb-2">
                        <Txt variant="detail-label">
                          Attempts ({delivery.attempts.length})
                        </Txt>
                      </div>
                      {delivery.attempts.length === 0 ? (
                        <Txt variant="body" tone="secondary">
                          No attempts
                        </Txt>
                      ) : (
                        <div className="flex flex-col gap-2">
                          {delivery.attempts.map((attempt) => (
                            <div
                              key={attempt.id}
                              className="flex flex-col gap-1 border-b pb-2 last:border-b-0"
                            >
                              <div className="flex items-center justify-between">
                                <DateUI
                                  date={attempt.createdAt}
                                  variant="DATETIME_TZ"
                                />
                                <Txt
                                  variant="caption"
                                  tone={
                                    attempt.result === "SUCCESS"
                                      ? "success"
                                      : "danger"
                                  }
                                >
                                  {prettyPrintEnum(attempt.result)}
                                </Txt>
                              </div>
                              {attempt.postmarkMessageId && (
                                <Txt variant="caption" tone="secondary">
                                  Postmark ID:{" "}
                                  <span className="font-mono">
                                    {attempt.postmarkMessageId}
                                  </span>
                                </Txt>
                              )}
                              {attempt.errorMessage && (
                                <Txt variant="caption" tone="danger">
                                  {attempt.errorMessage}
                                </Txt>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </AdminPageContainer>
  );
}
